import React, { useState, useEffect } from 'react';
import { User } from '../api/supabaseEntities.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Label } from "../components/ui/label.jsx";
import { Input } from "../components/ui/input.jsx";
import { Button } from "../components/ui/button.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { Switch } from "../components/ui/switch.jsx";
import { ScrollArea } from "../components/ui/scroll-area.jsx";
import { useToast } from "../hooks/useToast.js";
import {
  Settings2,
  User as UserIcon,
  Mail,
  Bell,
  Shield,
  Languages,
  Palette,
  Clock,
  Check,
  X,
  Database,
  RefreshCw,
  Save,
  HardDrive,
  Download
} from 'lucide-react';
import MySQLSchema from '../components/tools/MySQLSchema.jsx';
import PostgresSchema from '../components/tools/PostgresSchema.jsx';
import ToastContainer from '../components/ui/ToastContainer.jsx';

export default function Settings() {
  // Use toast hook
  const { toast, toasts, dismiss } = useToast();
  
  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // State for settings
  const [notifications, setNotifications] = useState({
    email: true,
    browser: true,
    sla: true,
    assignments: true,
    messages: true,
    updates: true
  });
  
  const [appearance, setAppearance] = useState({
    compactMode: false,
    animationsEnabled: true,
    autoHideSidebar: false
  });
  
  const [language, setLanguage] = useState('pt-BR');
  
  const [security, setSecurity] = useState({
    twoFactorEnabled: false,
    sessionTimeout: 60 // minutes
  });

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await User.me();
      setUser(userData);
      
      // Load saved preferences if they exist
      if (userData.preferences) {
        if (userData.preferences.notifications) {
          setNotifications(userData.preferences.notifications);
        }
        if (userData.preferences.appearance) {
          setAppearance(userData.preferences.appearance);
        }
        if (userData.preferences.language) {
          setLanguage(userData.preferences.language);
        }
        if (userData.preferences.security) {
          setSecurity(userData.preferences.security);
        }
      }
      
      setLoaded(true);
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const saveSettings = async () => {
    try {
      setIsSaving(true);
      
      const updatedPreferences = {
        notifications,
        appearance,
        language,
        security
      };
      
      await User.updateMyUserData({ preferences: updatedPreferences });
      
      if (toast) {
        toast({
          title: "Configurações salvas",
          description: "Suas preferências foram atualizadas com sucesso",
          variant: "success"
        });
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      if (toast) {
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar as configurações",
          variant: "destructive"
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
          <p className="text-muted-foreground">
            Gerenciar as preferências e configurações do sistema
          </p>
        </div>
        <Button onClick={saveSettings} disabled={isSaving}>
          {isSaving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Salvar configurações
        </Button>
      </div>

      <Tabs defaultValue="notifications">
        <TabsList className="grid grid-cols-5 mb-8">
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Notificações</span>
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Aparência</span>
          </TabsTrigger>
          <TabsTrigger value="language" className="flex items-center gap-2">
            <Languages className="h-4 w-4" />
            <span className="hidden sm:inline">Idioma</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Segurança</span>
          </TabsTrigger>
          <TabsTrigger value="database" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Banco de dados</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Preferências de Notificação</CardTitle>
              <CardDescription>
                Configure como e quando você deseja receber notificações do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Notificações por e-mail</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber e-mails sobre atualizações importantes
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.email}
                    onCheckedChange={(checked) => setNotifications({...notifications, email: checked})}
                  />
                </div>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Notificações no navegador</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber notificações do navegador quando a janela estiver em segundo plano
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.browser}
                    onCheckedChange={(checked) => setNotifications({...notifications, browser: checked})}
                  />
                </div>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Alertas de SLA</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber notificações quando um SLA estiver próximo do limite
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.sla}
                    onCheckedChange={(checked) => setNotifications({...notifications, sla: checked})}
                  />
                </div>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Atribuição de chamados</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber notificações quando um chamado for atribuído a você
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.assignments}
                    onCheckedChange={(checked) => setNotifications({...notifications, assignments: checked})}
                  />
                </div>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Mensagens</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber notificações para novas mensagens no chat
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.messages}
                    onCheckedChange={(checked) => setNotifications({...notifications, messages: checked})}
                  />
                </div>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Atualizações do sistema</Label>
                    <p className="text-sm text-muted-foreground">
                      Receber notificações sobre atualizações e novidades no sistema
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.updates}
                    onCheckedChange={(checked) => setNotifications({...notifications, updates: checked})}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>Aparência</CardTitle>
              <CardDescription>
                Personalize a aparência do sistema de acordo com suas preferências
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Modo compacto</Label>
                    <p className="text-sm text-muted-foreground">
                      Reduzir espaçamento e tamanho dos elementos para exibir mais conteúdo
                    </p>
                  </div>
                  <Switch 
                    checked={appearance.compactMode}
                    onCheckedChange={(checked) => setAppearance({...appearance, compactMode: checked})}
                  />
                </div>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Animações</Label>
                    <p className="text-sm text-muted-foreground">
                      Ativar animações na interface do usuário
                    </p>
                  </div>
                  <Switch 
                    checked={appearance.animationsEnabled}
                    onCheckedChange={(checked) => setAppearance({...appearance, animationsEnabled: checked})}
                  />
                </div>
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Ocultar barra lateral automaticamente</Label>
                    <p className="text-sm text-muted-foreground">
                      Esconder a barra lateral automaticamente em telas menores
                    </p>
                  </div>
                  <Switch 
                    checked={appearance.autoHideSidebar}
                    onCheckedChange={(checked) => setAppearance({...appearance, autoHideSidebar: checked})}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="language">
          <Card>
            <CardHeader>
              <CardTitle>Idioma e Região</CardTitle>
              <CardDescription>
                Configure o idioma e as preferências regionais do sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label>Idioma do sistema</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <Button 
                      variant={language === 'pt-BR' ? 'default' : 'outline'} 
                      className="justify-start"
                      onClick={() => setLanguage('pt-BR')}
                    >
                      <span className="mr-2">🇧🇷</span>
                      Português (Brasil)
                    </Button>
                    <Button 
                      variant={language === 'en-US' ? 'default' : 'outline'} 
                      className="justify-start"
                      onClick={() => setLanguage('en-US')}
                    >
                      <span className="mr-2">🇺🇸</span>
                      English (US)
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Segurança</CardTitle>
              <CardDescription>
                Gerencie as configurações de segurança e privacidade
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Autenticação em dois fatores</Label>
                    <p className="text-sm text-muted-foreground">
                      Adicione uma camada extra de segurança à sua conta
                    </p>
                  </div>
                  <Switch 
                    checked={security.twoFactorEnabled}
                    onCheckedChange={(checked) => setSecurity({...security, twoFactorEnabled: checked})}
                  />
                </div>
                <Separator />
                
                <div>
                  <Label>Tempo limite da sessão (minutos)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Input
                      type="number"
                      min={15}
                      max={240}
                      value={security.sessionTimeout}
                      onChange={(e) => setSecurity({...security, sessionTimeout: parseInt(e.target.value) || 60})}
                      className="w-32"
                    />
                    <p className="text-sm text-muted-foreground">
                      Tempo de inatividade até que a sessão expire automaticamente
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="database">
          <Card>
            <CardHeader>
              <CardTitle>Banco de Dados</CardTitle>
              <CardDescription>
                Scripts de criação de banco de dados para MySQL e PostgreSQL
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs defaultValue="mysql">
                <TabsList>
                  <TabsTrigger value="mysql">MySQL</TabsTrigger>
                  <TabsTrigger value="postgres">PostgreSQL</TabsTrigger>
                </TabsList>
                <TabsContent value="mysql">
                  <MySQLSchema />
                </TabsContent>
                <TabsContent value="postgres">
                  <PostgresSchema />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </div>
  );
}
